import { useMemo } from 'react'
import { Box, Text } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { CatalogoBasePage, type ColumnConfig, type FieldConfig } from '../../../components/CatalogoBasePage'
import { rolesCatalogService } from '../../../api/services'
import type { RoleItem } from '../../../api/models/maestros.models'
import { useAuth } from '../../../contexts/AuthContext'
import { useHasPermission } from '../../../auth/PermissionContext'
import { themeColors } from '../../../theme'

const Badge = ({ value, trueColor = '#2a8a87', falseColor = '#b5aa9d', trueText = 'Sí', falseText = 'No' }: { value: boolean; trueColor?: string; falseColor?: string; trueText?: string; falseText?: string }) => (
  <Box
    display="inline-block"
    px={2}
    py={0.5}
    borderRadius="md"
    fontSize="10px"
    fontWeight="700"
    textTransform="uppercase"
    letterSpacing="0.05em"
    bg={`${value ? trueColor : falseColor}20`}
    color={value ? trueColor : falseColor}
  >
    {value ? trueText : falseText}
  </Box>
)

const fields: FieldConfig<RoleItem>[] = [
  { name: 'code', label: 'Código', type: 'text', required: true, placeholder: 'ej. operador' },
  { name: 'name', label: 'Nombre', type: 'text', required: true, placeholder: 'ej. Operador' },
  { name: 'description', label: 'Descripción', type: 'textarea', colSpan: 2, placeholder: 'Descripción del rol' },
  { name: 'isExternalRole', label: '¿Es rol externo?', type: 'checkbox', helpText: 'Los roles externos son visibles para usuarios no internos' },
]

export default function RolesPage() {
  const { user } = useAuth()
  const isPlatform = user?.isPlatform ?? false
  const navigate = useNavigate()
  const canAssignPermissions = useHasPermission('roles.assignPermissions')

  // Columns depend on isPlatform: only the platform tenant cares about the
  // IsSystemRole flag (global subdivision); tenants see Global vs their
  // Personalizado scope.
  const columns = useMemo<ColumnConfig<RoleItem>[]>(() => {
    const cols: ColumnConfig<RoleItem>[] = [
      { header: 'Código', render: (r) => <Text fontFamily="monospace" fontSize="sm" fontWeight="600" color={themeColors.text}>{r.code}</Text> },
      { header: 'Nombre', render: (r) => <Text fontSize="sm" color={themeColors.text}>{r.name}</Text> },
      { header: 'Descripción', render: (r) => <Text fontSize="sm" color={themeColors.textSecondary}>{r.description ?? '-'}</Text> },
      {
        header: 'Ámbito',
        render: (r) => (
          <Badge
            value={r.tenantId == null}
            trueColor="#0EA5E9"
            falseColor="#A855F7"
            trueText="Global"
            falseText="Personalizado"
          />
        ),
      },
    ]
    if (isPlatform) {
      cols.push({
        header: 'Sistema',
        render: (r) => <Badge value={r.isSystemRole} trueColor="#d49a2a" trueText="Sistema" falseText="Custom" />,
      })
    }
    cols.push({
      header: 'Externo',
      render: (r) => <Badge value={r.isExternalRole} trueColor="#3d6fa8" trueText="Externo" falseText="Interno" />,
    })
    return cols
  }, [isPlatform])

  // Lock rules:
  // — Platform users: nothing locked (they own globals AND can intervene on
  //   tenant rows when needed).
  // — Tenant users: every global row is read-only. Their own custom rows are
  //   editable. (The backend SaveChanges guard enforces the same rule, this
  //   is just the UI hint.)
  const isItemLocked = (r: RoleItem): boolean => {
    if (isPlatform) return false
    return r.tenantId == null
  }

  const lockReason = isPlatform
    ? 'Este registro está bloqueado.'
    : 'Los roles globales solo pueden ser modificados por el tenant principal (Kuntur).'

  return (
    <CatalogoBasePage<RoleItem>
      title="Roles"
      subtitle={
        isPlatform
          ? 'Gestiona los roles globales del sistema. Los roles personalizados son creados por cada organización.'
          : 'Gestiona los roles personalizados de tu organización. Los roles globales son provistos por el sistema y solo pueden visualizarse.'
      }
      entityName="rol"
      service={rolesCatalogService as never}
      columns={columns}
      fields={fields}
      keyExtractor={(r) => r.id}
      idExtractor={(r) => r.id}
      isItemLocked={isItemLocked}
      lockReason={lockReason}
      searchPlaceholder="Buscar por código o nombre..."
      defaultValues={{ isExternalRole: false }}
      createPermission="roles.create"
      updatePermission="roles.update"
      deletePermission="roles.delete"
      extraRowActions={(r) => canAssignPermissions
        ? [{ label: 'Permisos', onClick: () => navigate(`/app/admin/roles/${r.id}/permissions`) }]
        : []}
    />
  )
}
